/* eslint-disable @typescript-eslint/no-explicit-any */
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarFillIcon } from "@heroicons/react/24/solid";
import Tooltip from "@mui/material/Tooltip";
import {
  GridColumnHeaderParams,
  GridRenderEditCellParams,
} from "@mui/x-data-grid";
import { ComponentProps } from "react";
import { Balancer } from "react-wrap-balancer";
import { twMerge } from "tailwind-merge";

import { AnimatedLink } from "@/components/animated-link";
import { Translation } from "@/data/lang";
import { links } from "@/data/links";
import { Lang } from "@/i18n-config";
import MeanRevertingRadarDownIcon from "@/public/screener/Down";
import SurfingTrendFallIcon from "@/public/screener/Fall";
import SurfingTrendRiseIcon from "@/public/screener/Rise";
import MeanRevertingRadarUpIcon from "@/public/screener/Up";

import { ScoreBadge } from "ui/widget/badge";

import { MutateWatchlistWrapper } from "../components/MutateWatchlistWrapper";

const colorMapper = (text: string) => {
  if (["建倉", "New Buy"].includes(text)) return "text-rise";
  if (["清倉", "Sold Out"].includes(text)) return "text-fall";

  const number = parseFloat(text);
  if (isNaN(number) || number === 0) return "text-[#202020]";
  return number > 0 ? "text-rise" : "text-fall";
};

export const renderBuySellCell = (
  params: GridRenderEditCellParams<any, any, string>
) => {
  const value = (params?.value ?? "-") as string;
  return (
    <p
      className={twMerge(
        value.startsWith("S") && "text-red-500",
        value.startsWith("B") && "text-emerald-500"
      )}
    >
      {value}
    </p>
  );
};

export const renderCell = (props?: {
  fractionDigits?: number;
  showRiseFallColor?: boolean;
  formatter?: (value: number | string) => string;
  useLocaleString?: boolean;
}) =>
  function renderCell(params: GridRenderEditCellParams<any, any, number>) {
    const {
      fractionDigits = 0,
      showRiseFallColor = false,
      formatter = (value: unknown) => `${value}`,
      useLocaleString = true,
    } = props ?? {};

    const value = params?.value as number | undefined;
    if (value == null) return "-";

    if (typeof value === "string")
      return (
        <p className={twMerge(showRiseFallColor && colorMapper(value))}>
          {value}
        </p>
      );

    return (
      <p
        className={twMerge(
          showRiseFallColor && colorMapper(value.toFixed(fractionDigits))
        )}
      >
        {formatter(
          useLocaleString
            ? (+value.toFixed(fractionDigits)).toLocaleString()
            : +value.toFixed(fractionDigits)
        )}
      </p>
    );
  };

export const renderTextCell = (
  params: GridRenderEditCellParams<any, any, number>
) => {
  const value = params?.value;
  if (value == null) return "-";
  return (
    <Tooltip title={value}>
      <p className="truncate whitespace-nowrap">{value}</p>
    </Tooltip>
  );
};

export const renderMarketCapCell = (
  params: GridRenderEditCellParams<any, any, number>
) => {
  const value = (params?.value ?? 0) as number;
  return (
    <p className="text-sm">
      {Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      })
        .format(value)
        .replace(/([A-Z])/g, " $1")}
    </p>
  );
};

export const renderForceCell = (
  params: GridRenderEditCellParams<any, any, number>
) => {
  const value = (params?.value ?? 0) as number;

  return (
    <div className="flex justify-center">
      <ScoreBadge score={value} />
    </div>
  );
};

const getScoreBadgeColors = ({ score }: { score?: number }) => {
  if (score == null) return "bg-white border-zinc-300";
  return {
    "-2": "border-red-300 bg-red-200 text-red-800",
    "-1": "border-red-200 bg-red-100 text-red-700",
    0: "border-zinc-200 bg-zinc-100 text-zinc-600",
    1: "border-emerald-200 bg-emerald-100 text-emerald-700",
    2: "border-emerald-300 bg-emerald-200 text-emerald-800",
  }[score];
};
const getScoreBadgeText = ({
  score,
  t,
}: {
  score?: number;
  t: Translation;
}) => {
  if (score == null) return "-";
  if (score === -2) return t.screenerOptions.shared.strongSell;
  if (score === -1) return t.screenerOptions.shared.sell;
  if (score === 0) return t.screenerOptions.shared.hold;
  if (score === 1) return t.screenerOptions.shared.buy;
  if (score === 2) return t.screenerOptions.shared.strongBuy;
};
export const renderAnalystRatingCell = ({
  lang,
  t,
}: {
  lang: Lang;
  t: Translation;
}) =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    const value = params?.value;
    const basicStyles = getScoreBadgeColors({ score: value });
    const badgeText = getScoreBadgeText({ score: value, t });
    return (
      <div
        className={twMerge(
          "flex h-5 w-24 items-center justify-center whitespace-nowrap rounded-full border",
          lang === "zh" && "w-[76px]",
          basicStyles
        )}
      >
        <span className="text-xs font-medium">{badgeText}</span>
      </div>
    );
  };

export const renderSqueezeCell = ({ t }: { t: Translation }) =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    const powers = t.screener.cellPowers;
    const colors = ["#BDBDBD", "#FAA1A4", "#FF9800", "#F23645"];
    const value = (params?.value ?? 0) as number;

    return (
      <span
        className="relative rounded-full p-4"
        style={{ backgroundColor: colors[value] }}
      >
        <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white">
          {powers[value]}
        </p>
      </span>
    );
  };

const riseColor = "#1CBC75";
const fallColor = "#F23645";
export const renderSurfingCell = () =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    const isParamsRise = params?.value === 1;
    const color = isParamsRise ? riseColor : fallColor;
    return (
      <div
        className="flex h-8 w-8 items-center justify-center rounded-full"
        style={{ backgroundColor: color }}
      >
        {isParamsRise ? (
          <SurfingTrendRiseIcon className="[&>path:first-child]:stroke-white [&>path:last-child]:fill-white" />
        ) : (
          <SurfingTrendFallIcon className="[&>path:first-child]:stroke-white [&>path:last-child]:fill-white" />
        )}
      </div>
    );
  };

export const renderMeanRevertingRadarCell = () =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    const isParamsUp = params?.value === 1;
    const color = isParamsUp ? riseColor : fallColor;
    return (
      <div
        className="flex h-8 w-8 items-center justify-center rounded-full"
        style={{ backgroundColor: color }}
      >
        {isParamsUp ? (
          <MeanRevertingRadarUpIcon className="[&>path:first-child]:stroke-white [&>path:last-child]:stroke-white [&>path:nth-child(2)]:fill-white" />
        ) : (
          <MeanRevertingRadarDownIcon className="[&>path:first-child]:stroke-white [&>path:last-child]:stroke-white [&>path:nth-child(2)]:fill-white" />
        )}
      </div>
    );
  };

export const renderFollowCell = ({
  userWatchlist,
}: {
  userWatchlist: string[] | null;
}) =>
  function render(params: GridRenderEditCellParams<any, any, string>) {
    const symbol = (params?.id ?? "") as string;
    const isFollowed = userWatchlist?.includes(symbol) ?? false;

    return (
      <MutateWatchlistWrapper symbol={symbol} isInWatchlist={isFollowed}>
        <div className="relative">
          <StarIcon className="h-[30px] w-[30px] cursor-pointer text-zinc-400" />
          {isFollowed && (
            <StarFillIcon className="absolute left-0 top-0 h-[30px] w-[30px] animate-fadeIn cursor-pointer text-yellow-400" />
          )}
        </div>
      </MutateWatchlistWrapper>
    );
  };

export const renderLinkCell = ({ lang }: { lang: Lang }) =>
  function render(params: GridRenderEditCellParams<any, any, string>) {
    const link = (params?.value ?? "") as string;
    return (
      <AnimatedLink
        href={`/${lang}${links.myAnalysisOverview({ symbol: link })}`}
        target="_blank"
        rel="noreferrer"
      >
        {params?.value ?? "-"}
      </AnimatedLink>
    );
  };

export const renderBalanceHeader = (props?: ComponentProps<"p">) =>
  function render(params: GridColumnHeaderParams) {
    return (
      <Tooltip title={params.colDef.headerName ?? ""}>
        <p
          {...props}
          className={twMerge("w-full text-center font-bold", props?.className)}
        >
          <Balancer>{params.colDef.headerName}</Balancer>
        </p>
      </Tooltip>
    );
  };

const getColorClassName = (value: string | number) => {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  if (numberValue > 0) return "text-rise";
  if (numberValue < 0) return "text-fall";
  return "text-zinc-800";
};

export const renderHighLowCell = ({
  t,
  showColor = true,
  componentProps,
}: {
  t: Translation;
  showColor?: boolean;
  componentProps?: ComponentProps<"p">;
}) =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    // if value is 0 and key contains "high" should return "new high"
    // if value is 0 and key contains "low" should return "new low"
    // else return percent format
    const value = params.value;
    const key = params.colDef.field;
    if (value === 0) {
      if (key.includes("high")) return t.screenerOptions.shared.newHigh;
      if (key.includes("low")) return t.screenerOptions.shared.newLow;
    }
    const formattedValue = new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(value);
    const colorClassName = showColor ? getColorClassName(value) : undefined;
    return (
      <p
        {...componentProps}
        className={twMerge(colorClassName, componentProps?.className)}
      >
        {formattedValue}
      </p>
    );
  };

export const renderNumericalCell = ({
  componentProps,
  formatter,
  showColor = true,
}: {
  componentProps?: ComponentProps<"p">;
  formatter?: (value: number) => string;
  showColor?: boolean;
}) =>
  function render(params: GridRenderEditCellParams<any, any, number>) {
    const value = params.value;
    const renderValue = formatter
      ? formatter(value).replace(/^-0$/, "0")
      : value;
    const colorClassName = showColor
      ? getColorClassName(renderValue)
      : undefined;

    return (
      <p
        {...componentProps}
        className={twMerge(colorClassName, componentProps?.className)}
      >
        {renderValue}
      </p>
    );
  };
